import React, { FC } from "react";

import {
  ActionValue,
  addToBasketSelection,
  ComponentAugmentKey,
  DataViewKey,
  DOWNLOAD_MANAGER_FORMAT_BY,
  downloadManagerPreferenceFormatByState,
  downloadManagerSelectionSelector,
  KpkMedia,
  MEDIA_TYPE_RICH_ELEMENT,
  ShareManagerSelectionType,
  useCurrentUser,
  useShareManagerSelections,
  useShareToken,
  useThumbnailGeneratorAllowed,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  generateButtonSx,
  KButtonAction,
  useFontAwesomeIconSize,
  withSuspense,
} from "@keepeek/refront-components";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import DetailDownloadButton from "../../../components/detail/DetailDownloadButton";
import ShowAttachmentButton from "../../../components/detail/ShowAttachmentButton";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import logger from "../../../lib/logger-utils";
import { GetCustomerAugmentedComponent } from "../../../lib/overrides";
import { ThumbnailGenerator } from "../../../models/configuration/components/thumbnailGenerator";
import { useAttachments } from "../../../providers/attachment/hooks/attachments";
import { backContextState } from "../../../providers/backContext/atoms";
import { useConfiguration } from "../../../providers/config/hooks";
import { configSectionPageElementSelector } from "../../../providers/config/selectors";
import { thumbnailGeneratorModalState } from "../../ThumbnailGeneratorModal/atom";
import MediaDetailHeaderActionsLayout from "./MediaDetailHeaderActionsLayout";

export type MediaDetailHeaderActionsProps = {
  kpkMedia: KpkMedia;
  showAttachments: boolean;
  showAddToBasket: boolean;
  showUnitaryPublicShare: boolean;
} & BaseComponentProps;

const MediaDetailHeaderActions: FC<React.PropsWithChildren<MediaDetailHeaderActionsProps>> =
  function MetadataInfo({
    className,
    kpkMedia,
    showAttachments,
    showAddToBasket,
    showUnitaryPublicShare,
  }) {
    const { hasPublicShare, currentUser } = useCurrentUser();
    const theme = useTheme();
    const { t } = useTranslation();
    const setThumbnailGeneratorModalState = useSetRecoilState(thumbnailGeneratorModalState);
    const { actionButtonsColor } = useRecoilValue(configSectionPageElementSelector) ?? {
      actionButtons: undefined,
    };
    const buttonSx = {
      ...generateButtonSx({
        buttonStyle: actionButtonsColor,
        theme,
      }),
      // style for counter
      ...generateButtonSx({
        buttonStyle: {
          colors: {
            backgroundColor: actionButtonsColor?.colors?.backgroundColor,
            textColor: actionButtonsColor?.colors?.textColor,
            borderColor: actionButtonsColor?.colors?.textColor || theme.palette.primary.main,
          },
        },
        theme,
        styleName: `& .MuiBadge-badge`,
      }),
    };
    const thumbnailGeneratorConfig = useConfiguration<ThumbnailGenerator>(
      CONFIGURATION_SECTION_PATH.COMPONENTS_THUMBNAIL_GENERATOR,
    );
    const isThumbnailGeneratorAllowed = useThumbnailGeneratorAllowed(currentUser);

    const backContext = useRecoilValue(backContextState);
    const setSelectionToDownloadManager = useSetRecoilState(
      downloadManagerSelectionSelector(backContext.key || DataViewKey.SEARCH),
    );
    const setPreferenceFormat = useSetRecoilState(downloadManagerPreferenceFormatByState);
    const router = useRouter();
    const { isShare, shareTokenDownloadAuthorized } = useShareToken(
      router.pathname,
      router.query.tokenId,
    );
    const { attachments } = useAttachments({ elementId: kpkMedia.id });

    const { handleSelections } = useShareManagerSelections();

    const setSelectionToAdd = useSetRecoilState(addToBasketSelection);

    const handleActionClick = (action: ActionValue) => {
      switch (action) {
        case ActionValue.DOWNLOAD:
          setSelectionToDownloadManager([kpkMedia.id]);
          setPreferenceFormat(DOWNLOAD_MANAGER_FORMAT_BY.TYPE);
          break;
        case ActionValue.ADD_TO_BASKET:
          setSelectionToAdd([kpkMedia.id]);
          break;
        case ActionValue.SHOW_ATTACHMENT:
          setSelectionToDownloadManager([kpkMedia.id]);
          setPreferenceFormat(DOWNLOAD_MANAGER_FORMAT_BY.OTHER);
          break;
        case ActionValue.SHARE:
          handleSelections([
            {
              elements: [
                { id: kpkMedia.id, title: kpkMedia.title.value, mediaType: kpkMedia.mediaType },
              ],
              type: ShareManagerSelectionType.UNITARY_PUBLIC_SHARE,
            },
          ]);
          break;
        case ActionValue.SHOW_THUMBNAIL_GENERATOR:
          setThumbnailGeneratorModalState(kpkMedia.id);
          break;
        default:
          logger.error(`action ${action} not implemented`);
      }
    };

    const fontAwesomeIconSizeForActions = useFontAwesomeIconSize({
      belowSmIconName: "",
      aboveSmIconName: "",
    });

    const buttons: JSX.Element[] = [];
    if (
      kpkMedia.mediaType !== MEDIA_TYPE_RICH_ELEMENT &&
      (!isShare || (isShare && shareTokenDownloadAuthorized))
    ) {
      buttons.push(
        <DetailDownloadButton
          key={"downloadButton"}
          testId="downloadFromMediaDetail"
          onClick={() => handleActionClick(ActionValue.DOWNLOAD)}
          buttonName={ActionValue.DOWNLOAD}
          // Useful for some overrides
          element={kpkMedia}
          sx={{
            ...buttonSx,
          }}
        />,
      );
    }
    if (showAttachments && attachments && attachments.length > 0) {
      buttons.push(
        <ShowAttachmentButton
          key={"attachmentButton"}
          onClick={() => handleActionClick(ActionValue.SHOW_ATTACHMENT)}
          attachmentsCount={attachments.length}
          buttonName={ActionValue.SHOW_ATTACHMENT}
          sx={{
            ...buttonSx,
          }}
        />,
      );
    }
    if (showAddToBasket) {
      buttons.push(
        <KButtonAction
          key={"addToBasketButton"}
          sx={{
            ...buttonSx,
          }}
          onClick={() => handleActionClick(ActionValue.ADD_TO_BASKET)}
          aria-label={t("media-detail.action.add-to-basket.label")}
          actionButtonName={ActionValue.ADD_TO_BASKET}
          title={t("media-detail.action.add-to-basket.label")}
        >
          <KIcon
            className={`fa-solid fa-fw fa-basket-shopping-plus ${fontAwesomeIconSizeForActions}`}
          />
        </KButtonAction>,
      );
    }
    if (showUnitaryPublicShare && hasPublicShare) {
      buttons.push(
        <KButtonAction
          key={"shareButton"}
          sx={{
            ...buttonSx,
          }}
          onClick={() => handleActionClick(ActionValue.SHARE)}
          actionButtonName={ActionValue.SHARE}
          title={t("media-detail.action.unitary-public-share.label")}
        >
          <KIcon className={`fa-share fa-fw ${fontAwesomeIconSizeForActions}`} />
        </KButtonAction>,
      );
    }

    if (isThumbnailGeneratorAllowed(kpkMedia) && thumbnailGeneratorConfig?.enabled) {
      buttons.push(
        <KButtonAction
          key={"thumbnailGeneratorButton"}
          sx={{
            ...buttonSx,
          }}
          onClick={() => handleActionClick(ActionValue.SHOW_THUMBNAIL_GENERATOR)}
          actionButtonName={ActionValue.SHOW_THUMBNAIL_GENERATOR}
          title={t("media-detail.action.show-thumbnail-generator.label")}
        >
          <KIcon className={`fa-crop-simple fa-fw ${fontAwesomeIconSizeForActions}`} />
        </KButtonAction>,
      );
    }

    return (
      <GetCustomerAugmentedComponent
        overrideKey={ComponentAugmentKey.MediaDetailHeaderActions}
        props={{
          className: className,
          buttons: buttons,
          kpkMedia: kpkMedia,
        }}
        Node={MediaDetailHeaderActionsLayout}
      />
    );
  };

export default withSuspense(MediaDetailHeaderActions);
